/**
 * Frontend Connection Timeout and State Synchronization Service
 *
 * Provides comprehensive timeout handling and state synchronization for:
 * - HTTP request timeouts with automatic retry
 * - WebSocket message timeouts
 * - Connection state synchronization between frontend and backend
 * - Service availability monitoring
 * - Graceful degradation strategies
 */

import { getLangGraphBackendUrl } from '../utils/langgraphConfig.js';
import errorRecoveryService from './errorRecoveryService.js';

class ConnectionTimeoutService {
  constructor() {
    this.timeoutConfigs = {
      http: {
        short: 5000, // 5 seconds for simple requests
        medium: 15000, // 15 seconds for standard requests
        long: 30000, // 30 seconds for complex requests
        stream: 60000, // 60 seconds for streaming requests
      },
      websocket: {
        ping: 10000, // 10 seconds for ping response
        message: 30000, // 30 seconds for message response
        connect: 10000, // 10 seconds for connection establishment
      },
      service: {
        health: 5000, // 5 seconds for health checks
        status: 3000, // 3 seconds for status checks
      },
    };

    // Active timeout tracking
    this.activeTimeouts = new Map();
    this.activeRequests = new Map();
    this.pendingPromises = new Map();

    // State synchronization
    this.connectionStates = new Map();
    this.lastSyncTime = null;
    this.syncInterval = null;

    // Monitoring
    this.metrics = {
      requestTimeouts: 0,
      websocketTimeouts: 0,
      serviceTimeouts: 0,
      retryCount: 0,
      successCount: 0,
    };

    // Initialize state sync asynchronously
    this.initializeStateSync();
  }

  /**
   * Initialize connection state synchronization
   */
  async initializeStateSync() {
    try {
      // Start periodic state synchronization (reduced frequency)
      this.syncInterval = setInterval(() => {
        this.syncConnectionStates();
      }, 30000); // Sync every 30 seconds instead of 5

      // Monitor network connectivity
      window.addEventListener('online', () => {
        this.updateConnectionState('network', 'online');
        this.handleNetworkRecovery();
      });

      window.addEventListener('offline', () => {
        this.updateConnectionState('network', 'offline');
      });

      // Initialize MCP API listeners asynchronously to avoid circular dependency
      await this.initializeMCPListeners();
    } catch (error) {
      console.warn('Failed to initialize connection state sync:', error);
    }
  }

  /**
   * Get MCP API instance (lazy loading to avoid circular dependency)
   */
  async getMCPAPI() {
    if (!this._mcpAPI) {
      try {
        const MCP_API = await import('../api/mcp.api.js');
        this._mcpAPI = MCP_API.default;
      } catch (error) {
        console.warn('Failed to load MCP API:', error);
        return null;
      }
    }
    return this._mcpAPI;
  }

  /**
   * Initialize MCP API listeners (lazy loading to avoid circular dependency)
   */
  async initializeMCPListeners() {
    try {
      const MCP_API = await this.getMCPAPI();

      if (MCP_API?.wsClient) {
        // Listen for connection state changes
        MCP_API.wsClient.on('connected', (data) => {
          this.updateConnectionState('websocket', 'connected', data);
        });

        MCP_API.wsClient.on('disconnected', (data) => {
          this.updateConnectionState('websocket', 'disconnected', data);
        });

        MCP_API.wsClient.on('connecting', () => {
          this.updateConnectionState('websocket', 'connecting');
        });

        console.log('✅ MCP API listeners initialized successfully');
      } else {
        console.warn('MCP WebSocket client not available, skipping MCP listeners');
      }
    } catch (error) {
      console.warn('Failed to initialize MCP listeners:', error);
      // Continue without MCP listeners - service can still function
    }
  }

  /**
   * Update connection state and notify listeners
   */
  updateConnectionState(service, state, data = {}) {
    const previousState = this.connectionStates.get(service);
    this.connectionStates.set(service, {
      state,
      timestamp: Date.now(),
      data,
    });

    // Notify state change
    this.emit('state_change', {
      service,
      state,
      previousState: previousState?.state,
      data,
    });

    console.log(`🔄 Connection state: ${service} -> ${state}`, data);
  }

  /**
   * Execute HTTP request with timeout and retry logic
   */
  async executeWithTimeout(url, options = {}, config = {}) {
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const timeoutConfig = {
      timeout: this.timeoutConfigs.http.medium,
      retries: 3,
      retryDelay: 1000,
      ...config,
    };

    return this._executeRequestWithTimeout(requestId, url, options, timeoutConfig);
  }

  async _executeRequestWithTimeout(requestId, url, options, config) {
    this.activeRequests.set(requestId, {
      startTime: Date.now(),
      url,
      config,
    });

    let lastError = null;

    for (let attempt = 1; attempt <= config.retries; attempt++) {
      try {
        const result = await this._makeRequestWithTimeout(requestId, url, options, config.timeout);

        // Success
        this.metrics.successCount++;
        this.activeRequests.delete(requestId);

        return result;
      } catch (error) {
        lastError = error;
        this.metrics.retryCount++;

        console.warn(`⚠️ Request attempt ${attempt} failed:`, error.message);

        // Check if we should retry
        if (attempt < config.retries && this._shouldRetry(error, attempt)) {
          const delay = this._calculateRetryDelay(attempt, config.retryDelay);
          console.log(`⏳ Retrying request in ${delay}ms (attempt ${attempt + 1}/${config.retries})`);

          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        break;
      }
    }

    // All attempts failed
    this.metrics.requestTimeouts++;
    this.activeRequests.delete(requestId);
    throw lastError;
  }

  async _makeRequestWithTimeout(requestId, url, options, timeout) {
    return new Promise(async (resolve, reject) => {
      // Create timeout
      const timeoutId = setTimeout(() => {
        reject(new Error(`Request timeout after ${timeout}ms`));
      }, timeout);

      // Store timeout reference
      this.activeTimeouts.set(requestId, timeoutId);

      try {
        let response;

        if (url.startsWith('http')) {
          // HTTP request
          response = await fetch(url, {
            ...options,
            signal: AbortSignal.timeout(timeout),
          });
        } else {
          // MCP API request
          const method = options.method || 'GET';
          const config = { ...options };

          // Remove fetch-specific options
          delete config.signal;

          const MCP_API = await this.getMCPAPI();
          if (MCP_API) {
            switch (method.toUpperCase()) {
              case 'GET':
                response = await MCP_API._get(url, config);
                break;
              case 'POST':
                response = await MCP_API._post(url, options.body || {}, config);
                break;
              case 'PUT':
                response = await MCP_API._put(url, options.body || {}, config);
                break;
              case 'DELETE':
                response = await MCP_API._delete(url, config);
                break;
              default:
                throw new Error(`Unsupported method: ${method}`);
            }
          } else {
            throw new Error('MCP API not available');
          }
        }

        clearTimeout(timeoutId);
        this.activeTimeouts.delete(requestId);

        resolve(response);
      } catch (error) {
        clearTimeout(timeoutId);
        this.activeTimeouts.delete(requestId);
        reject(error);
      }
    });
  }

  /**
   * Execute WebSocket request with timeout
   */
  async executeWebSocketRequest(method, params = {}, timeout = this.timeoutConfigs.websocket.message) {
    const MCP_API = await this.getMCPAPI();
    if (MCP_API && !MCP_API.isWebSocketConnected()) {
      await MCP_API.waitForConnection(5000);
    }

    return new Promise(async (resolve, reject) => {
      const requestId = `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const MCP_API = await this.getMCPAPI();

      if (!MCP_API || !MCP_API.wsClient) {
        reject(new Error('MCP WebSocket not available'));
        return;
      }

      // Set timeout
      const timeoutId = setTimeout(() => {
        MCP_API.wsClient.off('response', responseHandler);
        reject(new Error(`WebSocket request timeout after ${timeout}ms`));
      }, timeout);

      const responseHandler = (data) => {
        if (data.id === requestId) {
          clearTimeout(timeoutId);
          resolve(data.result);
        }
      };

      // Send request
      MCP_API.wsClient.sendRequest(method, params).then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      }).catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
    });
  }

  /**
   * Check service availability with timeout
   */
  async checkServiceAvailability(service = 'all', timeout = this.timeoutConfigs.service.health) {
    const services = service === 'all' ? ['backend', 'websocket', 'mcp', 'network'] : [service];
    const results = {};

    for (const svc of services) {
      try {
        switch (svc) {
          case 'backend':
            results[svc] = await this._checkBackendHealth(timeout);
            break;
          case 'websocket':
            results[svc] = await this._checkWebSocketHealth(timeout);
            break;
          case 'mcp':
            results[svc] = await this._checkMCPHealth(timeout);
            break;
          case 'network':
            results[svc] = await this._checkNetworkHealth(timeout);
            break;
        }
      } catch (error) {
        results[svc] = { available: false, error: error.message };
        this.metrics.serviceTimeouts++;
      }
    }

    return results;
  }

  async _checkBackendHealth(timeout) {
    try {
      // Try /health endpoint first
      let fullUrl = `${getLangGraphBackendUrl()}/health`;
      let response;

      try {
        response = await fetch(fullUrl, {
          method: 'GET',
          signal: AbortSignal.timeout(timeout),
        });
      } catch (healthError) {
        // If /health fails, try /api/v1/mcp/status as fallback
        console.log('⚠️ /health endpoint failed, trying MCP status endpoint:', healthError.message);
        fullUrl = `${getLangGraphBackendUrl()}/api/v1/mcp/status`;
        response = await fetch(fullUrl, {
          method: 'GET',
          signal: AbortSignal.timeout(timeout),
        });
      }

      const isAvailable = response.ok;
      this.updateConnectionState('backend', isAvailable ? 'online' : 'offline', { status: response.status });
      return { available: isAvailable, status: response.status };
    } catch (error) {
      console.error('❌ Backend health check failed:', error);
      this.updateConnectionState('backend', 'offline', { error: error.message });
      return { available: false, error: error.message };
    }
  }

  async _checkWebSocketHealth(timeout) {
    try {
      // Import mcpService and MCP_API dynamically to avoid circular dependency
      const mcpService = await import('./mcpService.js').then((m) => m.default);
      const MCP_API = await import('../api/mcp.api.js').then((m) => m.default);

      // Check WebSocket connection status from MCP_API (more reliable)
      const isWebSocketConnected = MCP_API.isWebSocketConnected();

      if (isWebSocketConnected) {
        this.updateConnectionState('websocket', 'connected', { latency: 0 });
        return { available: true, latency: 0 };
      }

      // Fallback: check mcpService.wsConnected
      if (mcpService.wsConnected) {
        this.updateConnectionState('websocket', 'connected', { latency: 0 });
        return { available: true, latency: 0 };
      }

      // Try to initialize mcpService if not connected
      if (!isWebSocketConnected && mcpService.servers && mcpService.servers.length === 0) {
        try {
          console.log('🔧 Initializing MCP service for WebSocket health check...');
          await mcpService.initialize();
          // Give it a moment to connect
          await new Promise((resolve) => setTimeout(resolve, 1000));

          const isConnectedAfterInit = MCP_API.isWebSocketConnected() || mcpService.wsConnected;
          if (isConnectedAfterInit) {
            this.updateConnectionState('websocket', 'connected', { latency: 0 });
            return { available: true, latency: 0 };
          }
        } catch (initError) {
          console.warn('Failed to initialize mcpService:', initError);
        }
      }

      this.updateConnectionState('websocket', 'disconnected', { error: 'WebSocket not connected' });
      return { available: false, error: 'WebSocket not connected' };
    } catch (error) {
      console.error('WebSocket health check error:', error);
      this.updateConnectionState('websocket', 'unavailable', { error: error.message });
      return { available: false, error: error.message };
    }
  }

  async _checkMCPHealth(timeout) {
    try {
      const MCP_API = await this.getMCPAPI();
      const status = MCP_API ? await MCP_API.getServerStatusWithTimeout(timeout) : null;
      const isAvailable = status?.running || false;
      this.updateConnectionState('mcp', isAvailable ? 'available' : 'unavailable', status);
      return { available: isAvailable, status };
    } catch (error) {
      this.updateConnectionState('mcp', 'unavailable', { error: error.message });
      return { available: false, error: error.message };
    }
  }

  async _checkNetworkHealth(timeout) {
    try {
      // Network connectivity is handled by browser online/offline events
      // For health check, we can test basic connectivity
      const isOnline = navigator.onLine;
      this.updateConnectionState('network', isOnline ? 'online' : 'offline', { isOnline });
      return { available: isOnline, isOnline };
    } catch (error) {
      this.updateConnectionState('network', 'unknown', { error: error.message });
      return { available: false, error: error.message };
    }
  }

  /**
   * Handle network recovery
   */
  async handleNetworkRecovery() {
    console.log('🔄 Network recovery initiated');

    // Check service availability
    const availability = await this.checkServiceAvailability('all', 5000);

    // Update connection states
    Object.entries(availability).forEach(([service, status]) => {
      this.updateConnectionState(service, status.available ? 'available' : 'unavailable', status);
    });

    // If services are available, sync connection states
    if (Object.values(availability).some((s) => s.available)) {
      await this.syncConnectionStates();
    }

    this.emit('network_recovered', availability);
  }

  /**
   * Synchronize connection states with backend
   */
  async syncConnectionStates() {
    try {
      const backendState = await this.checkServiceAvailability('backend', 5000);

      if (backendState.backend.available) {
        // Get detailed backend status
        const healthData = await fetch(`${getLangGraphBackendUrl()}/health`, {
          method: 'GET',
          signal: AbortSignal.timeout(3000),
        }).then((res) => res.json()).catch(() => null);

        // Compare frontend and backend states
        const wsState = this.connectionStates.get('websocket');
        const shouldReconnect = wsState?.state === 'disconnected'
                               && healthData.websocket?.connected;

        if (shouldReconnect) {
          console.log('🔄 Syncing WebSocket state - reconnecting');
          const MCP_API = await this.getMCPAPI();
          if (MCP_API) {
            await MCP_API.connectWebSocket();
          }
        }

        this.lastSyncTime = Date.now();
        this.emit('state_synced', { backend: healthData, frontend: this.getConnectionStates() });
      }
    } catch (error) {
      console.warn('⚠️ Failed to sync connection states:', error.message);
    }
  }

  /**
   * Get current connection states
   */
  getConnectionStates() {
    const states = {};
    this.connectionStates.forEach((value, key) => {
      states[key] = value;
    });
    return states;
  }

  /**
   * Get connection metrics
   */
  getMetrics() {
    return {
      ...this.metrics,
      activeRequests: this.activeRequests.size,
      activeTimeouts: this.activeTimeouts.size,
      lastSyncTime: this.lastSyncTime,
      connectionStates: this.getConnectionStates(),
    };
  }

  /**
   * Clean up resources
   */
  cleanup() {
    // Clear all timeouts
    this.activeTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.activeTimeouts.clear();

    // Cancel pending requests
    this.activeRequests.forEach((request, id) => {
      if (request.abortController) {
        request.abortController.abort();
      }
    });
    this.activeRequests.clear();

    // Clear sync interval
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    console.log('🧹 Connection timeout service cleaned up');
  }

  /**
   * Determine if an error should trigger a retry
   */
  _shouldRetry(error, attempt) {
    // Don't retry on client errors (4xx)
    if (error.status && error.status >= 400 && error.status < 500) {
      return false;
    }

    // Don't retry on certain error types
    if (error.message?.includes('abort')
        || error.message?.includes('cancel')) {
      return false;
    }

    return true;
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  _calculateRetryDelay(attempt, baseDelay) {
    const exponentialDelay = baseDelay * 2 ** (attempt - 1);
    const jitter = Math.random() * 0.1 * exponentialDelay;
    return Math.min(exponentialDelay + jitter, 30000); // Max 30 seconds
  }

  /**
   * Event system
   */
  on(event, handler) {
    if (!this.eventListeners) {
      this.eventListeners = new Map();
    }
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(handler);
  }

  off(event, handler) {
    if (this.eventListeners && this.eventListeners.has(event)) {
      const handlers = this.eventListeners.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners && this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }
}

// Global connection timeout service instance
export const connectionTimeoutService = new ConnectionTimeoutService();

// Convenience functions
export const executeWithTimeout = (url, options = {}, config = {}) => connectionTimeoutService.executeWithTimeout(url, options, config);

export const executeWebSocketRequest = (method, params = {}, timeout = 30000) => connectionTimeoutService.executeWebSocketRequest(method, params, timeout);

export const checkServiceAvailability = (service = 'all', timeout = 5000) => connectionTimeoutService.checkServiceAvailability(service, timeout);

export const getConnectionMetrics = () => connectionTimeoutService.getMetrics();

export const getConnectionStates = () => connectionTimeoutService.getConnectionStates();

// Export for use in components
export default connectionTimeoutService;
